import React from 'react';
import { useSEO } from '@/hooks/useSEO';
import { motion } from 'framer-motion';
import {
  Mail,
  MessageSquare,
  BookOpen,
  Send,
  Headphones,
} from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

const contactChannels = [
  {
    title: 'Email support',
    description: 'For product questions, billing, and general inquiries.',
    value: 'support@fiveai.co',
    href: 'mailto:support@fiveai.co',
    icon: Mail,
    gradient: 'from-primary/10 to-violet-500/10',
    iconBg: 'bg-primary/15 text-primary',
    cta: 'Send email',
  },
  {
    title: 'Partnerships & sales',
    description: 'Enterprise, learning & development, or partnership opportunities.',
    value: 'partners@fiveai.co',
    href: 'mailto:partners@fiveai.co',
    icon: Send,
    gradient: 'from-emerald-500/10 to-teal-500/10',
    iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    cta: 'Get in touch',
  },
  {
    title: 'Help & documentation',
    description: 'FAQs, guides, and how to get the most from your AI coach.',
    value: 'Help Center',
    href: '/coach',
    icon: BookOpen,
    gradient: 'from-amber-500/10 to-orange-500/10',
    iconBg: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    cta: 'Visit Help Center',
  },
  {
    title: 'Talk to your AI coach',
    description: 'Your personalized coach is available in-app for tips and guidance.',
    value: 'In-app chat',
    href: '/coach',
    icon: MessageSquare,
    gradient: 'from-violet-500/10 to-fuchsia-500/10',
    iconBg: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
    cta: 'Open Coach',
  },
];

const contactInfo = [
  { label: 'Support email', value: 'support@fiveai.co', href: 'mailto:support@fiveai.co' },
  { label: 'Partnerships', value: 'partners@fiveai.co', href: 'mailto:partners@fiveai.co' },
  { label: 'Website', value: 'fiveai.com', href: 'https://fiveai.com' },
];

export const ContactPage: React.FC = () => {
  useSEO({ title: 'Contact Five AI - Support, Sales & Partnerships', description: 'Get in touch with Five AI. Email support@fiveai.co for help, partners@fiveai.co for enterprise and partnership inquiries. We typically respond within 24 hours.', path: '/contact' });

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="Contact Five AI">
      <Header />
      <div className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
              >
                <Headphones className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">We're here to help</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">Get in</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                  touch
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                Have a question or feedback? Reach out via email or use your in-app AI coach for instant guidance.
              </p>
              <p className="text-sm text-muted-foreground/80">
                Support, partnerships, and help
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {contactChannels.map((channel, i) => {
                const Icon = channel.icon;
                const isExternal = channel.href.startsWith('mailto:') || channel.href.startsWith('http');
                return (
                  <motion.div
                    key={channel.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                  >
                    <Card
                      hover
                      className={`h-full p-6 sm:p-8 rounded-2xl border-2 bg-gradient-to-br ${channel.gradient} hover:border-primary/40 transition-all duration-300`}
                    >
                      <div className={`w-11 h-11 rounded-xl ${channel.iconBg} flex items-center justify-center mb-5 shrink-0`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3">{channel.title}</h2>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-3">{channel.description}</p>
                      <p className="text-sm font-medium text-foreground mb-6">{channel.value}</p>
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="border-primary/30 text-primary hover:bg-primary/10 rounded-xl"
                      >
                        <a href={channel.href} target={isExternal && channel.href.startsWith('http') ? '_blank' : undefined} rel={isExternal && channel.href.startsWith('http') ? 'noopener noreferrer' : undefined}>
                          {channel.cta}
                        </a>
                      </Button>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-16 text-center"
            >
              <div className="inline-flex flex-wrap items-center justify-center gap-6 sm:gap-10 py-6 px-6 rounded-2xl bg-muted/50 border-2 border-border">
                {contactInfo.map(({ label, value, href }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith('http') ? '_blank' : undefined}
                    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {label}: <span className="font-medium text-foreground">{value}</span>
                  </a>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
